import { Box, CircularProgress } from "@mui/material"
import type { FC } from "react"
import { useRef, useState } from "react"

import { useGetQuotedProducts } from "../../hooks/useGetQuotedProducts"

import { QuotedProductsTable } from "./QuotedProductsTable"

import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { AddProductAssociationDialog } from "@/screens/CustomerProducts/components/AssociateProductDialog/AddProductAssociationDialog"

interface QuotedProductsTabPanelProps {
  companyId: string
  shipToLocationId: string
  query: string
  onAssociationComplete: () => void
}

export const QuotedProductsTabPanel: FC<QuotedProductsTabPanelProps> = ({
  companyId,
  shipToLocationId,
  query,
  onAssociationComplete,
}) => {
  const { quotedProducts, isFetching, error, loadMore } = useGetQuotedProducts({
    companyId,
    query,
    shipToLocationId,
  })
  const [associatingProductId, setAssociatingProductId] = useState<number | undefined>()

  const containerRef = useRef<HTMLDivElement>(null)
  const [throttledLoadMore] = useThrottle(() => loadMore(), 200)
  const handleScroll = useScrollLoadMore({
    fetching: isFetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: containerRef,
  })

  const isInitialLoading = isFetching && !quotedProducts.length

  const handleAssociate = (productId: number) => setAssociatingProductId(productId)

  const handleAssociationClose = (shouldRefetch?: boolean) => {
    setAssociatingProductId(undefined)
    if (shouldRefetch) onAssociationComplete()
  }

  if (isInitialLoading) {
    return (
      <Box className='flex items-center justify-center p-8'>
        <CircularProgress data-testid='customer-orders.product-selection-dialog.quoted-loading-indicator' />
      </Box>
    )
  }

  return (
    <Box className='flex min-h-0 flex-1 overflow-y-auto'>
      <QuotedProductsTable
        data={quotedProducts}
        isFetching={isFetching}
        error={error}
        containerRef={containerRef}
        onScroll={handleScroll}
        onAssociate={handleAssociate}
      />
      {associatingProductId !== undefined && (
        <AddProductAssociationDialog
          companyId={companyId}
          initialProductId={associatingProductId}
          initialShipToLocationId={shipToLocationId}
          onClose={handleAssociationClose}
        />
      )}
    </Box>
  )
}
